import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from 'antd';
import { HomeOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../../store/authStore';
import { getDefaultRouteForRole } from '../../../utils/permissions';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component that catches React errors and displays a fallback UI
 * Implements Requirements 13 and 24 for error handling and visual feedback
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('Error caught by ErrorBoundary:', error);
    console.error('Error info:', errorInfo);
    
    // Update state with error info
    this.setState({
      errorInfo,
    });

    // In production, you might want to send this to an error tracking service
    // Example: logErrorToService(error, errorInfo);
  }

  handleHome = () => {
    const { isAuthenticated, user } = useAuthStore.getState();
    window.location.href = isAuthenticated && user ? getDefaultRouteForRole(user.role) : '/';
  };

  render() {
    if (this.state.hasError) {
      const isAuthenticated = useAuthStore.getState().isAuthenticated;

      return (
        <div className={styles.screen} role="alert">
          <div className={styles.panel}>
            <WarningOutlined className={styles.icon} aria-hidden="true" />
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.body}>
              An unexpected error stopped this page from loading. Reloading usually fixes it.
              If it keeps happening, email{' '}
              <a href="mailto:support@rongeka.africa">support@rongeka.africa</a> and tell us
              what you were doing.
            </p>
            <div className={styles.actions}>
              <Button type="primary" size="large" icon={<ReloadOutlined />} onClick={() => window.location.reload()}>
                Reload Page
              </Button>
              <Button size="large" icon={<HomeOutlined />} onClick={this.handleHome} className={styles.secondary}>
                {isAuthenticated ? 'Go to Your Dashboard' : 'Back to Home'}
              </Button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={styles.details}>
                <summary>Error Details (Development Only)</summary>
                <p><strong>Error:</strong> {this.state.error.toString()}</p>
                {this.state.errorInfo && <pre>{this.state.errorInfo.componentStack}</pre>}
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
